import { ClassesIcon } from 'Components/common/Icons/ClassesIcon'
import { Classes } from 'Components/tasks/Classes/Classes'
import { Studio } from 'Core/Studio'
import { ITask } from 'Core/TaskAdapter'
import { WorkbenchUiAccessorType } from 'Core/Workbench'
import { flatten, isEmpty } from 'lodash'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { TextAnnotationsUI } from 'Tasks/TextAnnotationTask/UI/TextAnnotationsTaskUI'
import {
    AnnotationSelection,
    AnnotationSelectionRange,
    ClassAnnotation,
    ClassItem,
} from 'Types/Classes.types'
import {
    AttributeDefinitionType,
    ContainerType,
    TaskConfigType,
    TextAnnotationDataType,
} from 'Types/Tasks.types'
import { createReactElement, UpdatableReactElement } from 'shared/utils/react.utils'
import { Comments } from 'Components/tasks/Comments/Comments'
import { CommentsIcon } from 'Components/common/Icons/CommentsIcon'
import { KeyboardIcon } from 'Components/common/Icons/KeyboardIcon'
import KeyboardShortcuts from 'Components/tasks/KeyboardShortcuts/KeyboardShortcuts'
import { getNodeFromPath } from 'Utils/dom.utils'
import { isAlmostEqual } from 'Utils/common.utils'
import { TextAnnotationContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/TextAnnotationContainerStrategy'

export class TextAnnotationTask
    extends CommonTask<ContainerType.TextAnnotation, TextAnnotationDataType>
    implements ITask<TaskConfigType<ContainerType.TextAnnotation, TextAnnotationDataType>>
{
    private maxHeightIndex: number
    private selectedClass: ClassItem | null
    private classesUi?: UpdatableReactElement
    private commentsUi?: UpdatableReactElement
    private annotationList: ClassAnnotation[]
    private focusedAnnotationId: string | null
    private classes: ClassItem[]

    constructor(protected studio: Studio) {
        const textAnnotationContainerStrategy = new TextAnnotationContainerStrategy()
        super(studio, textAnnotationContainerStrategy)
        textAnnotationContainerStrategy.setContext({ task: this })

        this.maxHeightIndex = 0
        this.selectedClass = null
        this.annotationList = []
        this.focusedAnnotationId = null
        this.classes = []
    }

    public initialize(
        config: TaskConfigType<ContainerType.TextAnnotation, TextAnnotationDataType>
    ) {
        this.studio.workbench.enqueueUiUpdate(workbenchUi => {
            this.ui = createReactElement(TextAnnotationsUI, {
                content:
                    config.assignment.task.input_container.data.data.content_entity?.content
                        .value ?? '',
                getMaxHeightIndex: () => this.maxHeightIndex,
                getSelectedClass: () => this.selectedClass,
                getAnnotationList: () => this.annotationList,
                addAnnotationToList: (selection: AnnotationSelection) =>
                    this.addAnnotationToList(selection),
                deleteAnnotationFromList: (index: number) => {
                    this.deleteAnnotationsFromList(index)
                },
                onChangeContainerDimensions: () => {
                    this.resetRowSelectionRanges()
                    this.calculatePositionsOfAnnotations()
                },
                getClassList: () => this.classes,
                onInit: () => this.initialiseAnnotations(),
                updateAnnotationComment: (annotationId: string, comment: string) => {
                    this.annotationList.find(
                        annotation => annotation.id === annotationId
                    )!.comment = comment
                    this.ui?.updater.forceUpdate()
                    this.commentsUi?.updater.forceUpdate()
                    this.validateAnnotationsComments()
                },
                getFocusedAnnotationId: () => this.focusedAnnotationId,
                setFocusedAnnotationId: (id: string | null) => {
                    this.focusedAnnotationId = id
                    this.ui?.updater.forceUpdate()
                    this.commentsUi?.updater.forceUpdate()
                },
            })

            this.addCommonWorkbenchComponents(workbenchUi, config)
            this.addKeyboardShortcutsElement(workbenchUi)
            this.initialiseClassesList(config)

            // add classes component
            this.addClassesElement(workbenchUi)

            // add rate/review if attributes are present
            if (this.hasEditableAttributes(config)) {
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    groupKey: 'quality-evaluation',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: { useInCollection: true },
                })
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.EXTRA_INFO,
                    groupKey: 'extra-info',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: { useInCollection: true },
                })
            }

            this.addCommentsElement(workbenchUi)

            workbenchUi.rightSidebar.addDivider()
        })
    }

    protected keyBindingActions = (event: KeyboardEvent) => {
        if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault()
            console.log('Save command triggered')
            this.saveTask(true)
        }
        if (event.key === 'Escape' && this.selectedClass !== null) {
            event.preventDefault()
            this.selectedClass = null
            this.ui?.updater.forceUpdate()
            this.classesUi?.updater.forceUpdate()
        }
    }

    private initialiseClassesList = (
        config: TaskConfigType<ContainerType.TextAnnotation, TextAnnotationDataType>
    ) => {
        this.classes = config.assignment.task.classes.map(classItem => ({
            id: classItem.class_id,
            name: classItem.name,
            description: classItem.description,
            label: classItem.label,
            color: classItem.color,
            isVisible: true,
        }))
    }

    /**
     * On load, fetch the annotations from the draft output container and initialise the annotations
     * in the UI. For each annotation, we create a range and add it to the selection and add to our
     * annotation list
     */
    private initialiseAnnotations = () => {
        const existingAnnotations: {
            id: string
            classId: string
            startIndex: number
            endIndex: number
            comment: string
            fieldPath: string
        }[] = (
            this.taskState.getDraftOutputContainer()?.data?.data.response?.text_annotations ?? []
        )
            .map(textAnnotation => {
                return (
                    textAnnotation.annotations?.map(annotation => ({
                        id: annotation.value.id,
                        classId:
                            this.classes.find(
                                classItem => classItem.name === annotation.value.class_name
                            )?.id ?? '',
                        startIndex: annotation.value.start_index,
                        endIndex: annotation.value.end_index,
                        comment: annotation.value.extra_info.comment_attribute_name.value,
                        fieldPath: textAnnotation.target.field_path,
                    })) ?? []
                )
            })
            .flat()

        existingAnnotations.forEach(annotation => {
            const range = document.createRange()
            const selection = window.getSelection()
            selection?.removeAllRanges()

            const containerElement = getNodeFromPath(annotation.fieldPath)?.firstChild

            if (containerElement) {
                // Set the start and end of the range in the text node
                range.setStart(containerElement, annotation.startIndex)
                range.setEnd(containerElement, annotation.endIndex)

                // Add the range to the current selection
                selection?.addRange(range)
            }

            this.annotationList.push({
                id: annotation.id,
                classId: annotation.classId,
                selection: {
                    startIndex: annotation.startIndex,
                    endIndex: annotation.endIndex,
                    individualRowSelectionRange: Array.from(range.getClientRects()).map(rect => ({
                        positionStartX: rect.x,
                        positionStartY: rect.y,
                        positionWidth: rect.width,
                        index: 0,
                    })),
                    range,
                    fieldPath: annotation.fieldPath,
                },
                comment: annotation.comment,
            })
        })

        this.calculatePositionsOfAnnotations()
        this.validateAnnotationsComments()
        this.clearSelection()
        this.commentsUi?.updater.forceUpdate()
        this.ui?.updater.forceUpdate()
    }

    private addAnnotationToList(selection: AnnotationSelection) {
        const annotationId = crypto.randomUUID()
        this.annotationList.push({
            id: annotationId,
            classId: this.selectedClass?.id ?? '',
            selection,
            comment: '',
        })
        this.calculatePositionsOfAnnotations()
        this.validateAnnotationsComments()
        this.scrollToAnnotation(annotationId)
        this.clearSelection()
        this.classesUi?.updater.forceUpdate()
        this.ui?.updater.forceUpdate()
    }

    private deleteAnnotationsFromList(index: number) {
        this.annotationList.splice(index, 1)
        this.calculatePositionsOfAnnotations()
        this.validateAnnotationsComments()
        this.classesUi?.updater.forceUpdate()
        this.ui?.updater.forceUpdate()
        this.commentsUi?.updater.forceUpdate()
    }

    private scrollToAnnotation = async (id: string) => {
        const mainContent = document.getElementById('main-content')!
        const annotation = this.annotationList.find(annotation => annotation.id === id)

        if (annotation) {
            const targetY = Math.max(
                annotation.selection.individualRowSelectionRange[0].positionStartY -
                    200 +
                    mainContent.scrollTop,
                0
            )

            mainContent.scrollTo({
                top: targetY,
                behavior: 'smooth',
            })
        }
    }

    /**
     * Reset the coordinates for each of the hidden annotations so that they don't mess with
     * calculating overlapping annotations
     */
    private clearSelectionRangeOfHiddenAnnotations = () => {
        this.annotationList.forEach(annotation => {
            if (!this.classes.find(classItem => classItem.id === annotation.classId)?.isVisible) {
                annotation.selection.individualRowSelectionRange.forEach(range => {
                    range.index = -1
                    range.positionStartX = 0
                    range.positionStartY = 0
                })
            }
        })
    }

    /**
     * After any change in container width or scroll, the x and y coordinates of each of the
     * selection would also change. So on each such change, we recalculate the position in the
     * DOM for each selection range.
     */
    private resetRowSelectionRanges = () => {
        const rootElement = document.querySelector('#text-annotations-content')

        if (!(rootElement instanceof HTMLElement)) {
            return
        }

        this.getVisibleAnnotationsList().forEach(annotation => {
            const containerElement = getNodeFromPath(annotation.selection.fieldPath)?.firstChild

            const range = document.createRange()
            const selection = window.getSelection()
            selection?.removeAllRanges()

            if (containerElement) {
                // Set the start and end of the range in the text node
                range.setStart(containerElement, annotation.selection.startIndex)
                range.setEnd(containerElement, annotation.selection.endIndex)

                // Add the range to the current selection
                selection?.addRange(range)
            }

            annotation.selection.individualRowSelectionRange = Array.from(
                range.getClientRects()
            ).map(rect => ({
                positionStartX: rect.x,
                positionStartY: rect.y,
                positionWidth: rect.width,
                index: 0,
            }))
        })
        this.clearSelection()
    }

    /**
     * Calculates the positions of annotations based on their selection ranges.
     * This method sorts the annotations by their position width and groups them by rows.
     * It then assigns indices to the annotations based on their position and checks for overlaps.
     * Larger annotations have a lower index than smaller annotations if they overlap.
     * Finally, it updates the maxHeightIndex and triggers a UI update if necessary.
     */
    private calculatePositionsOfAnnotations = () => {
        if (!this.annotationList) {
            return
        }

        this.clearSelectionRangeOfHiddenAnnotations()
        this.recalculateSelectionRangeOfAnnotations()

        const annotationsRangeList = flatten(
            this.getVisibleAnnotationsList().map(annotation => {
                return annotation.selection.individualRowSelectionRange.map(selectionRange => {
                    return {
                        class: this.classes.find(classItem => classItem.id === annotation.classId),
                        selectionRange,
                        range: annotation.selection.range,
                        id: annotation.id,
                    }
                })
            })
        )

        annotationsRangeList.sort((a, b) => {
            return b.selectionRange.positionWidth - a.selectionRange.positionWidth
        })

        const annotationsGroupedByRows: {
            [key: number]: {
                class: ClassItem | undefined
                selectionRange: AnnotationSelectionRange
                id: string
            }[]
        } = {}

        // Group annotations based on their y coordinates i.e. annotations in the same line
        annotationsRangeList.forEach(item => {
            if (!annotationsGroupedByRows[item.selectionRange.positionStartY]) {
                annotationsGroupedByRows[item.selectionRange.positionStartY] = []
            }
            annotationsGroupedByRows[item.selectionRange.positionStartY].push(item)
        })

        Object.values(annotationsGroupedByRows).forEach(group => {
            // Sort the annotations in the group by their total width. There can be multiple items for the same annotation if it spans multiple elements (for example, an anchor tag)
            group.sort((a, b) => {
                const totalWidthB = group.reduce((acc, item) => {
                    if (
                        isAlmostEqual(
                            b.selectionRange.positionStartY,
                            item.selectionRange.positionStartY
                        ) &&
                        b.id === item.id
                    ) {
                        return acc + item.selectionRange.positionWidth
                    } else {
                        return acc
                    }
                }, 0)

                const totalWidthA = group.reduce((acc, item) => {
                    if (
                        isAlmostEqual(
                            a.selectionRange.positionStartY,
                            item.selectionRange.positionStartY
                        ) &&
                        a.id === item.id
                    ) {
                        return acc + item.selectionRange.positionWidth
                    } else {
                        return acc
                    }
                }, 0)

                return totalWidthB - totalWidthA
            })

            const assignedIndices: {
                [index: number]: {
                    class: ClassItem | undefined
                    selectionRange: AnnotationSelectionRange
                }[]
            } = {}

            group.forEach(item => {
                let assignedIndex = -1

                for (let i = 0; i <= Object.keys(assignedIndices).length; i++) {
                    const currentIndexItems = assignedIndices[i] || []

                    // Check if this item overlaps with any items already assigned to this index
                    const overlaps = currentIndexItems.some(
                        existingItem =>
                            item.selectionRange.positionStartX <
                                existingItem.selectionRange.positionStartX +
                                    existingItem.selectionRange.positionWidth &&
                            item.selectionRange.positionStartX + item.selectionRange.positionWidth >
                                existingItem.selectionRange.positionStartX
                    )

                    // If no overlap, assign this index
                    if (!overlaps) {
                        assignedIndex = i
                        break
                    }
                }

                if (assignedIndex === -1) {
                    assignedIndex = Object.keys(assignedIndices).length
                }

                item.selectionRange.index = assignedIndex

                if (!assignedIndices[assignedIndex]) {
                    assignedIndices[assignedIndex] = []
                }
                assignedIndices[assignedIndex].push(item)
            })
        })

        this.recalculateMaxHeightIndex()
    }

    /**
     * Recalculates the maximum height index of the annotations.
     * This method iterates through the annotation list and updates the maxHeightIndex
     * if a higher index is found.
     * It also triggers a UI update and calls the recalculatePositionsOfAnnotations method.
     */
    private recalculateMaxHeightIndex() {
        let maxHeightIndex = 0
        this.getVisibleAnnotationsList().forEach(annotation => {
            annotation.selection.individualRowSelectionRange.forEach(range => {
                if (range.index + 1 > maxHeightIndex) {
                    maxHeightIndex = range.index + 1
                }
            })
        })

        this.maxHeightIndex = maxHeightIndex
        this.ui?.updater.forceUpdate()

        setTimeout(() => {
            this.recalculateSelectionRangeOfAnnotations()
        })
    }

    private recalculateSelectionRangeOfAnnotations = () => {
        this.getVisibleAnnotationsList().forEach(annotation => {
            annotation.selection.individualRowSelectionRange.forEach((range, index) => {
                range.positionStartY = annotation.selection.range.getClientRects()[index].y
                range.positionStartX = annotation.selection.range.getClientRects()[index].x
                range.positionWidth = annotation.selection.range.getClientRects()[index].width
            })
        })
        this.ui?.updater.forceUpdate()
    }

    private getVisibleAnnotationsList() {
        return this.annotationList.filter(
            annotation =>
                this.classes.find(classItem => classItem.id === annotation.classId)?.isVisible
        )
    }

    private clearSelection() {
        const selection = window.getSelection()
        if (selection) {
            selection.removeAllRanges()
        }
    }

    private addClassesElement(workbenchUiAccessor: WorkbenchUiAccessorType) {
        this.classesUi = createReactElement(Classes, {
            classes: this.classes,
            setClassVisibility: async (id: string, isVisible: boolean) => {
                if (id === 'all') {
                    this.classes.forEach(item => (item.isVisible = isVisible))
                } else {
                    const classItem = this.classes.find(item => item.id === id)
                    if (classItem) {
                        classItem.isVisible = isVisible
                    }
                }
                this.calculatePositionsOfAnnotations()
                this.ui?.updater.forceUpdate()
                this.classesUi?.updater.forceUpdate()
            },
            getActiveClass: () => this.selectedClass?.id ?? null,
            setActiveClass: (id: string) => {
                if (this.isReadOnly) {
                    return
                }
                const selectedClass = this.classes.find(item => item.id === id)
                if (selectedClass?.id !== this.selectedClass?.id) {
                    this.selectedClass = this.classes.find(item => item.id === id) || null
                    this.ui?.updater.forceUpdate()
                    this.classesUi?.updater.forceUpdate()
                }
            },
            getAnnotationList: () => this.annotationList,
            setClassFocus: (id: string, isFocused: boolean) => {
                this.classes.forEach(item => {
                    item.isVisible = !isFocused || item.id === id
                })
                this.calculatePositionsOfAnnotations()
                this.ui?.updater.forceUpdate()
                this.classesUi?.updater.forceUpdate()
            },
        })

        workbenchUiAccessor.rightSidebar.addItem({
            id: 'classes',
            element: this.classesUi.element,
            title: 'Class',
            icon: ClassesIcon,
        })
    }

    private addCommentsElement(workbenchUiAccessor: WorkbenchUiAccessorType) {
        if (
            !this.initialConfig?.assignment.task.config.text_annotations_config
                ?.are_comments_enabled
        ) {
            return
        }
        this.commentsUi = createReactElement(Comments, {
            getComments: () =>
                this.annotationList
                    .filter(annotation => annotation.comment)
                    .map(annotation => ({
                        originalContent: annotation.comment,
                        content: annotation.comment,
                        annotationId: annotation.id,
                        classItem: this.classes.find(
                            classItem => classItem.id === annotation.classId
                        )!,
                        isEditable: false,
                    })),
            getClasses: () => this.classes,
            updateAnnotationComment: (annotationId: string, comment: string) => {
                this.annotationList.find(annotation => annotation.id === annotationId)!.comment =
                    comment
                this.validateAnnotationsComments()
                this.commentsUi?.updater.forceUpdate()
                this.ui?.updater.forceUpdate()
            },
            deleteAnnotationComment: (annotationId: string) => {
                this.annotationList.find(annotation => annotation.id === annotationId)!.comment = ''
                this.validateAnnotationsComments()
                this.commentsUi?.updater.forceUpdate()
                this.ui?.updater.forceUpdate()
            },
            getFocusedAnnotationId: () => this.focusedAnnotationId,
            setFocusedAnnotationId: (id: string | null) => {
                this.focusedAnnotationId = id
                this.ui?.updater.forceUpdate()
                this.commentsUi?.updater.forceUpdate()
            },
            onClickComment: (id: string) => {
                this.scrollToAnnotation(id)
            },
            isReadOnly: () => this.isReadOnly,
        })

        workbenchUiAccessor.rightSidebar.addItem({
            id: 'comments',
            element: this.commentsUi.element,
            title: 'Comments',
            icon: CommentsIcon,
        })
    }

    private validateAnnotationsComments() {
        if (
            this.initialConfig?.assignment.task.config.text_annotations_config
                ?.are_comments_optional ||
            !this.initialConfig?.assignment.task.config.text_annotations_config
                ?.are_comments_enabled
        ) {
            return
        }

        const invalidAnnotations = this.annotationList.filter(annotation =>
            isEmpty(annotation.comment.trim())
        )
        if (invalidAnnotations.length > 0) {
            this.studio.inputManager.addViolation({
                id: 'annotations-comments',
                type: 'error',
                message: 'Please add comments for all annotations',
            })
        } else {
            this.studio.inputManager.removeViolation('annotations-comments', 'error')
        }
    }

    private addKeyboardShortcutsElement(workbenchUiAccessor: WorkbenchUiAccessorType) {
        const keyboardShortcutsUI = createReactElement(KeyboardShortcuts, {})

        workbenchUiAccessor.leftSidebar.addItem({
            id: 'keyboard-shortcuts',
            element: keyboardShortcutsUI.element,
            title: 'Keyboard Shortcuts',
            icon: KeyboardIcon,
        })
    }

    getAnnotationList() {
        return this.annotationList
    }

    getClasses() {
        return this.classes
    }
}
